// Offline activity utility functions
import { enqueue } from './offlineQueue.js';

/**
 * Generate a temporary attempt ID for offline use
 */
export function generateTempAttemptId() {
  return `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/**
 * Check if the app is currently online
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Start an activity attempt with offline support
 */
export async function startActivityAttempt(activityId, attemptData, options = {}) {
  const tempAttemptId = generateTempAttemptId();
  
  if (isOnline()) {
    try {
      // Try online first
      const headers = { 'Content-Type': 'application/json' };
      if (options.accessToken) {
        headers['Authorization'] = `Bearer ${options.accessToken}`;
      }
      
      const response = await fetch(`/api/activities/${activityId}/attempts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ attempt_data: attemptData })
      });
      
      if (response.ok) {
        const data = await response.json();
        return { success: true, attempt: data.attempt, offline: false };
      }
      // A 4xx is a server REJECTION (auth, access, validation) — queueing it
      // as "offline" would show a false success and retry a permanent failure
      // forever. Only network failures / 5xx fall through to the queue.
      if (response.status >= 400 && response.status < 500) {
        const data = await response.json().catch(() => ({}));
        return { success: false, error: data.error || `Request failed (${response.status})`, status: response.status, offline: false };
      }
      throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      console.warn('Failed to start attempt online, queuing for offline sync:', error);
      // Fall through to offline handling
    }
  }
  
  // Queue for offline sync
  await enqueue({
    type: 'activity_start_attempt',
    payload: { activityId, attempt_data: attemptData },
    tempAttemptId
  });
  
  // Return a mock attempt object for offline use
  const mockAttempt = {
    id: tempAttemptId,
    activity_id: activityId,
    status: 'in_progress',
    attempt_data: attemptData,
    started_at: new Date().toISOString(),
    _offline: true
  };
  
  return { success: true, attempt: mockAttempt, offline: true };
}

/**
 * Complete an activity attempt with offline support
 */
export async function completeActivityAttempt(activityId, attemptId, completionData, options = {}) {
  // Check if this is a temporary offline attempt
  const isOfflineAttempt = attemptId.startsWith('temp_');
  
  if (isOnline() && !isOfflineAttempt) {
    try {
      // Try online first for real attempts
      const headers = { 'Content-Type': 'application/json' };
      if (options.accessToken) {
        headers['Authorization'] = `Bearer ${options.accessToken}`;
      }
      
      const response = await fetch(`/api/activities/${activityId}/attempts/${attemptId}/complete`, {
        method: 'POST',
        headers,
        body: JSON.stringify(completionData)
      });
      
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        window.dispatchEvent(new CustomEvent('astra:activity-completed', {
          detail: { activityId, attemptId, ...data }
        }));
        return { success: true, offline: false, ...data };
      }
      // A 4xx is a server REJECTION (expired auth, attempt-state conflict,
      // validation) — queueing it would tell the learner "submitted offline,
      // will sync" while online, and the queue would retry the same permanent
      // failure forever. Only network failures / 5xx fall through to the queue.
      if (response.status >= 400 && response.status < 500) {
        const data = await response.json().catch(() => ({}));
        return { success: false, error: data.error || `Request failed (${response.status})`, status: response.status, offline: false };
      }
      throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      console.warn('Failed to complete attempt online, queuing for offline sync:', error);
      // Fall through to offline handling
    }
  }
  
  // For offline attempts or failed online attempts, queue for sync
  let finalAttemptId = attemptId;
  
  if (isOfflineAttempt) {
    // Check if we have a real attempt ID from a previous sync
    const realAttemptId = localStorage.getItem(`astra.temp.attempt.${attemptId}`);
    if (realAttemptId) {
      finalAttemptId = realAttemptId;
      localStorage.removeItem(`astra.temp.attempt.${attemptId}`);
    } else {
      // Still offline, keep the temp ID and it will be resolved when the start attempt syncs
      finalAttemptId = attemptId;
    }
  }
  
  await enqueue({
    type: 'activity_complete_attempt',
    payload: { activityId, attemptId: finalAttemptId, completionData }
  });
  window.dispatchEvent(new CustomEvent('astra:activity-completed', {
    detail: { activityId, attemptId: finalAttemptId, offline: true }
  }));
  
  return { success: true, offline: true };
}

/**
 * Load activity data with offline fallback
 */
export async function loadActivityData(endpoint, options = {}) {
  if (isOnline()) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (options.accessToken) {
        headers['Authorization'] = `Bearer ${options.accessToken}`;
      }
      
      const response = await fetch(endpoint, { headers });
      
      if (response.ok) {
        const data = await response.json();
        // Cache the data for offline use
        if ('caches' in window) {
          try {
            const cache = await caches.open('runtime-v4');
            await cache.put(endpoint, new Response(JSON.stringify(data), {
              headers: { 'Content-Type': 'application/json' }
            }));
          } catch (e) {
            console.warn('Failed to cache activity data:', e);
          }
        }
        return { success: true, data, offline: false };
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.warn('Failed to load activity data online, trying cache:', error);
      // Fall through to cache check
    }
  }
  
  // Try to load from cache
  if ('caches' in window) {
    try {
      const cache = await caches.open('runtime-v4');
      const cached = await cache.match(endpoint);
      if (cached) {
        const data = await cached.json();
        return { success: true, data, offline: true };
      }
    } catch (e) {
      console.warn('Failed to load from cache:', e);
    }
  }
  
  return { success: false, error: 'No data available offline', offline: true };
}

/**
 * Show offline notification to user
 */
export function showOfflineNotification(message = 'You are offline. Your progress will be saved and synced when you reconnect.') {
  // Create or update offline notification
  let notification = document.getElementById('offline-activity-notification');
  
  if (!notification) {
    notification = document.createElement('div');
    notification.id = 'offline-activity-notification';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f59e0b;
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 1000;
      max-width: 300px;
      font-size: 14px;
      transition: all 0.3s ease;
    `;
    document.body.appendChild(notification);
  }
  
  notification.textContent = message;
  notification.style.display = 'block';
  
  // Auto-hide after 5 seconds
  setTimeout(() => {
    if (notification && notification.parentNode) {
      notification.style.display = 'none';
    }
  }, 5000);
}

/**
 * Hide offline notification
 */
export function hideOfflineNotification() {
  const notification = document.getElementById('offline-activity-notification');
  if (notification) {
    notification.style.display = 'none';
  }
}

/**
 * Listen for online/offline events and show notifications
 */
export function initOfflineNotifications() {
  window.addEventListener('offline', () => {
    showOfflineNotification('You are offline. Activity progress will be saved locally and synced when you reconnect.');
  });
  
  window.addEventListener('online', () => {
    hideOfflineNotification();
    // Show brief reconnected message
    showOfflineNotification('Back online! Syncing your progress...');
    setTimeout(hideOfflineNotification, 3000);
  });
}
